import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { PlanService, PlanFeatures } from '../services/plan.service';
import { DairyAddonService } from '../services/dairy-addon.service';
import { catchError, map, Observable, of } from 'rxjs';

/**
 * PlanGuard — Route-level UX guard for plan/feature restrictions.
 *
 * Apply to routes via the `data` property:
 *
 *   {
 *     path: 'ledger',
 *     canActivate: [AuthGuard, PlanGuard],
 *     data: { feature: 'ledger', requiredPlan: 'ADVANCE' },
 *     loadChildren: ...
 *   }
 *
 * NOTE: This guard is for UX only. Server-side PHP enforces the real security.
 */
@Injectable({ providedIn: 'root' })
export class PlanGuard implements CanActivate {

    constructor(private planService: PlanService, private router: Router, private addons: DairyAddonService) {}

    canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {
        const feature     = route.data?.['feature']      as keyof PlanFeatures | undefined;
        const requiredPlan = route.data?.['requiredPlan'] as string | undefined;
        if (feature === 'production' || feature === 'storage_locations') {
            if (!route.data['writeAddon'] && route.queryParamMap.get('history') === '1') return true;
            return this.addons.refresh().pipe(
                map(() => {
                    if (this.planService.isFeatureAllowed(feature)) return true;
                    this.router.navigate(['/store'], { queryParams: { category: 'DAIRY_ADDON' } });
                    return false;
                }),
                catchError(() => {
                    this.router.navigate(['/store'], { queryParams: { category: 'DAIRY_ADDON' } });
                    return of(false);
                })
            );
        }

        let allowed = true;

        // Check feature flag
        if (feature) {
            allowed = this.planService.isFeatureAllowed(feature);
        }

        // Check required plan level
        if (allowed && requiredPlan) {
            const planLevels: Record<string, number> = { BASIC: 1, STARTER: 1, ADVANCE: 2, PREMIUM: 3 };
            const userLevel     = this.planService.getPlanLevel();
            const requiredLevel = planLevels[requiredPlan.toUpperCase()] ?? 2;
            allowed = userLevel >= requiredLevel;
        }

        if (!allowed) {
            this.router.navigate(['/dashboard'], {
                queryParams: {
                    upgrade: true,
                    feature: feature ?? '',
                    required: requiredPlan ?? 'ADVANCE',
                }
            });
            return false;
        }

        return true;
    }
}
